import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../utils/api';
import { Package, Eye, Copy, Filter, Link2, Search, X } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function AdminOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const STATUS_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'ordered', label: 'Ordered' },
    { value: 'processing', label: 'Processing' },
    { value: 'dispatched', label: 'Dispatched' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'refunded', label: 'Refunded' },
    { value: 'pending', label: 'Pending' },
  ];

  // Per-status counts for the filter chips
  const statusCounts = useMemo(() => {
    const counts = { all: orders.length };
    for (const o of orders) {
      const s = (o.order_status || o.status || 'ordered').toLowerCase();
      counts[s] = (counts[s] || 0) + 1;
    }
    return counts;
  }, [orders]);

  // Filtered list shown in the table — by status AND by free-text search
  const filteredOrders = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return orders.filter((o) => {
      // Status match
      if (statusFilter !== 'all') {
        const s = (o.order_status || o.status || 'ordered').toLowerCase();
        if (s !== statusFilter) return false;
      }
      // Free-text match against everything the row visually displays + a few
      // useful identifiers (order_data.displayName, product, material, link code, etc.)
      if (q) {
        const s = o.shipping_address || {};
        const b = o.billing_address || {};
        const od = o.order_data || {};
        const link = o.dynamic_link_details || {};

        const parts = [
          // Customer
          s.firstName, s.lastName, `${s.firstName || ''} ${s.lastName || ''}`,
          b.firstName, b.lastName, `${b.firstName || ''} ${b.lastName || ''}`,
          s.email, b.email,
          s.phone, b.phone,
          s.company, b.company,
          // Product / customization (visible & related)
          od.displayName,
          od.cardName,
          od.cardType,
          od.businessName,
          od.companyName,
          od.customerName,
          od.name,
          od.selectedMaterial?.name,
          od.selectedColor?.name,
          od.selectedTag?.name,
          // Link
          link.code,
          // Identifiers
          o.transaction_id,
          o.session_id,
        ];

        const haystack = parts.filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [orders, statusFilter, searchTerm]);

  useEffect(() => {
    checkAuth();
    fetchOrders();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      setOrders(response.data.orders);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleDuplicateOrder = async (order) => {
    const shortId = order.transaction_id.slice(0, 8);
    const confirmMsg = `Duplicate order #${shortId}?\n\nThis will create a brand-new order for the same customer with the same product and details. The new order will start as "Ordered" with no dynamic link assigned.`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setDuplicatingId(order.transaction_id);
    setError('');

    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(
        `${BACKEND_URL}/api/admin/orders/${order.transaction_id}/duplicate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(response.data?.detail || 'Failed to duplicate order');
      }

      // Refresh the list so the new order shows up at the top
      await fetchOrders();
    } catch (err) {
      setError(err.message || 'Failed to duplicate order');
    } finally {
      setDuplicatingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'dispatched':
        return 'bg-purple-100 text-purple-800';
      case 'ordered':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'refunded':
        return 'bg-gray-100 text-gray-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading orders...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {!loading && orders.length === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-navy mb-2">No Orders Yet</h3>
            <p className="text-gray-600">Orders will appear here once customers place them.</p>
          </div>
        )}

        {!loading && orders.length > 0 && (
          <>
            {/* Status Filter + Search */}
            <div className="bg-white rounded-2xl shadow-sm p-4 mb-4">
              {/* Search row */}
              <div className="relative mb-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, product, display name, email, order id..."
                  className="w-full pl-9 pr-9 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 text-gray-500"
                    title="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm font-semibold text-navy">
                  <Filter className="w-4 h-4" />
                  <span>Filter by status:</span>
                </div>

                {/* Chip-style filter buttons */}
                <div className="flex items-center gap-2 flex-wrap">
                  {STATUS_OPTIONS.map((opt) => {
                    const isActive = statusFilter === opt.value;
                    const count = statusCounts[opt.value] || 0;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setStatusFilter(opt.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                          isActive
                            ? 'bg-navy text-white border-navy'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {opt.label}
                        <span
                          className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] ${
                            isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Native select fallback for very small screens */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="md:hidden ml-auto px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label} ({statusCounts[opt.value] || 0})
                    </option>
                  ))}
                </select>
              </div>

              <p className="text-xs text-gray-500 mt-3">
                Showing <span className="font-semibold text-navy">{filteredOrders.length}</span>{' '}
                of <span className="font-semibold text-navy">{orders.length}</span> orders
                {statusFilter !== 'all' && (
                  <>
                    {' '}— filtered by{' '}
                    <span className="font-semibold text-navy capitalize">{statusFilter}</span>
                  </>
                )}
                {searchTerm && (
                  <>
                    {' '}— matching "<span className="font-semibold text-navy">{searchTerm}</span>"
                  </>
                )}
              </p>
            </div>

            {filteredOrders.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-navy mb-2">No Orders Match This Filter</h3>
                <p className="text-gray-600">
                  Try selecting a different status or clear the filter to see all orders.
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Product Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Material
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Dynamic Link
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => {
                    // Determine product name based on order type
                    let productName = 'N/A';
                    let materialName = 'N/A';
                    
                    if (order.order_data) {
                      // Check if it's a digital card
                      if (order.order_data.cardName) {
                        productName = order.order_data.cardName;
                      } else if (order.order_data.cardType) {
                        // Review card
                        productName = order.order_data.cardType;
                      }
                      
                      // Get material name
                      if (order.order_data.selectedMaterial && order.order_data.selectedMaterial.name) {
                        materialName = order.order_data.selectedMaterial.name;
                      }
                    }
                    
                    return (
                      <tr key={order.transaction_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-navy">
                          #{order.transaction_id.slice(0, 8)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="font-semibold">{productName}</div>
                          {order.order_data?.displayName && (
                            <div className="text-xs text-gray-500 mt-1">{order.order_data.displayName}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {materialName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {order.order_data?.quantity || 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          ${order.amount}
                          <div className={`text-xs font-medium mt-0.5 ${order.payment_status === 'paid' ? 'text-green-600' : 'text-amber-500'}`}>
                            {order.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.order_status || order.status)}`}>
                            {(order.order_status || order.status || 'ordered').charAt(0).toUpperCase() + (order.order_status || order.status || 'ordered').slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {order.dynamic_link_details?.code ? (
                            <a
                              href={`${window.location.origin}/${order.dynamic_link_details.code}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 font-mono text-xs border border-blue-200"
                              title="Open dynamic link"
                            >
                              <Link2 className="w-3 h-3" />
                              /{order.dynamic_link_details.code}
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400">— not linked —</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(order.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-4">
                            <button
                              onClick={() => navigate(`/admin/orders/${order.transaction_id}`)}
                              className="text-navy hover:text-navy/80 font-medium flex items-center gap-1"
                            >
                              <Eye className="w-4 h-4" />
                              View Details
                            </button>
                            <button
                              onClick={() => handleDuplicateOrder(order)}
                              disabled={duplicatingId === order.transaction_id}
                              className="text-navy hover:text-navy/80 font-medium flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Duplicate this order as a new order"
                            >
                              <Copy className="w-4 h-4" />
                              {duplicatingId === order.transaction_id ? 'Duplicating...' : 'Duplicate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
